import { Component, Input, Output, EventEmitter, inject, OnInit, OnDestroy, ElementRef, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { UserListService, UserListCreatePayload, UserListItemPayload } from '../../../../core/services/users/userList.service';
import { ToastService } from '../../../../layout/toast/toast.service';
import { LanguageService } from '../../../../core/services/language/language.service';
import { getStoredAuthToken } from '../../../../core/auth/auth-token';
import { compressImageToWebPDataUrl } from '../../../../core/utils/image-compressor';

@Component({
  selector: 'app-kebab-list-component',
  imports: [CommonModule, LucideAngularModule, FormsModule],
  templateUrl: './kebab-list-component.html',
  styleUrl: './kebab-list-component.scss'
})
export class KebabListComponent implements OnInit, OnDestroy {

  @Input() data: any;
  @Output() toggleChange = new EventEmitter();

  private userListService = inject(UserListService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private el = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);

  showPanel = false;
  isLoading = false;
  userLists: any[] = [];
  activeTab: 'create' | 'existing' = 'create';

  createForm = {
    name: '',
    dscr: '',
    image: '',
    backdrop_image: ''
  };
  createImagePreview = '';
  createBackdropPreview = '';
  isCreating = false;

  private backdropEl: HTMLDivElement | null = null;
  private panelEl: HTMLElement | null = null;
  private boundKeyHandler = this.onKeyDown.bind(this);
  private boundClickHandler: ((e: Event) => void) | null = null;
  private boundInputHandlers: { el: HTMLElement; event: string; fn: (e: Event) => void }[] = [];

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.removePortal();
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('keydown', this.boundKeyHandler);
    }
  }

  onAddToListClick() {
    if (isPlatformBrowser(this.platformId)) {
      const token = getStoredAuthToken();
      if (!token) {
        this.toast.warning(
          this.languageService.t('loginRequiredTitle') || 'Login Required',
          this.languageService.t('loginRequiredMessage') || 'Please log in to perform this activity.'
        );
        this.toggleChange.emit();
        this.router.navigate(['/auth'], { queryParams: { redirect: this.router.url || '/' } });
        return;
      }
    }
    if (this.showPanel) {
      this.removePortal();
      this.showPanel = false;
      return;
    }
    this.showPanel = true;

    // Initialize createForm with current movie poster/backdrop as prefilled option
    this.createForm = {
      name: '',
      dscr: '',
      image: '',
      backdrop_image: ''
    };
    this.createImagePreview = '';
    this.createBackdropPreview = '';
    this.activeTab = 'existing';

    this.createPortal();
    this.fetchLists();
  }

  private createPortal() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.removePortal();
    document.addEventListener('keydown', this.boundKeyHandler);

    // Backdrop - matching .list-modal-backdrop in user-lists
    this.backdropEl = document.createElement('div');
    this.backdropEl.className = 'list-modal-backdrop';
    this.backdropEl.addEventListener('click', (e) => {
      if (e.target === this.backdropEl) {
        this.removePortal();
        this.showPanel = false;
        this.toggleChange.emit();
      }
    });

    // Panel - matching .list-modal in user-lists
    this.panelEl = document.createElement('section');
    this.panelEl.className = 'list-modal';
    this.panelEl.addEventListener('click', (e) => e.stopPropagation());

    this.backdropEl.appendChild(this.panelEl);
    document.body.appendChild(this.backdropEl);

    this.renderPanelContent();
  }

  private renderPanelContent() {
    if (!this.panelEl) return;
    this.panelEl.innerHTML = this.buildPanelHTML();
    this.bindPanelEvents();
    this.applyPanelStyles();
  }

  private applyPanelStyles() {
    if (!this.backdropEl) return;
    if (this.backdropEl.querySelector('style#klp-styles')) return;

    const style = document.createElement('style');
    style.id = 'klp-styles';
    style.textContent = `
      .list-modal-backdrop {
        position: fixed;
        inset: 0;
        z-index: 2147483646;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1.5rem;
        background: rgba(4, 13, 20, 0.72);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        animation: modalFadeIn 0.2s ease-out;
        box-sizing: border-box;
      }

      @keyframes modalFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .list-modal {
        position: relative;
        width: min(580px, 94vw);
        max-height: 88vh;
        overflow-y: auto;
        border-radius: 22px;
        background: #ffffff;
        padding: 2rem 2.2rem;
        box-shadow:
          0 24px 70px rgba(0, 0, 0, 0.35),
          0 4px 20px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(229, 231, 235, 0.9);
        animation: modalZoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        box-sizing: border-box;
        font-family: var(--font-body, 'Montserrat', sans-serif);
      }

      @keyframes modalZoomIn {
        from {
          opacity: 0;
          transform: scale(0.95) translateY(10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }

      .modal-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 1.5rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid #f1f5f9;
      }

      .modal-header .eyebrow {
        margin: 0 0 0.3rem;
        font-size: 0.75rem;
        font-weight: 800;
        letter-spacing: 0.08em;
        color: #12b886;
        text-transform: uppercase;
      }

      .modal-header h2 {
        margin: 0;
        font-size: 1.35rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -0.02em;
      }

      .close-button {
        background: transparent;
        border: none;
        color: #64748b;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        padding: 0.3rem 0.6rem;
        border-radius: 8px;
        transition: all 0.15s ease;
      }

      .close-button:hover {
        background: #f1f5f9;
        color: #0f172a;
      }

      .klp-subtitle {
        margin: 0.35rem 0 0;
        font-size: 0.85rem;
        color: #64748b;
        font-weight: 500;
        line-height: 1.4;
      }

      .klp-top-action-bar {
        margin-bottom: 1.25rem;
      }

      .klp-new-list-cta-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.55rem;
        padding: 0.75rem 1.25rem;
        border-radius: 12px;
        background: linear-gradient(135deg, rgba(18, 184, 134, 0.08), rgba(56, 217, 169, 0.16));
        border: 1.5px dashed #12b886;
        color: #0ca678;
        font-family: inherit;
        font-size: 0.88rem;
        font-weight: 700;
        cursor: pointer;
        width: 100%;
        justify-content: center;
        box-sizing: border-box;
        transition: all 0.2s ease;
      }

      .klp-new-list-cta-btn:hover {
        background: #12b886;
        color: #ffffff;
        border-style: solid;
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(18, 184, 134, 0.25);
      }

      .klp-back-bar {
        margin-bottom: 1.25rem;
      }

      .klp-back-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
        border-radius: 999px;
        padding: 0.45rem 1.1rem;
        color: #334155;
        font-family: inherit;
        font-size: 0.82rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .klp-back-btn:hover {
        background: #e2e8f0;
        color: #0f172a;
      }

      .klp-item.is-added {
        background: #f8fafc;
        cursor: default;
        border-color: #cbd5e1;
      }

      .klp-item.is-added:hover {
        transform: none;
        box-shadow: none;
        border-color: #cbd5e1;
        background: #f8fafc;
      }

      .klp-empty-icon {
        margin-bottom: 0.75rem;
      }

      /* Tabs (when user has existing lists) */
      .modal-tabs {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1.25rem;
        background: #f8fafc;
        padding: 4px;
        border-radius: 12px;
        border: 1px solid #e2e8f0;
      }

      .modal-tab-btn {
        flex: 1;
        padding: 0.55rem 1rem;
        border-radius: 8px;
        border: none;
        background: transparent;
        font-size: 0.82rem;
        font-weight: 700;
        color: #64748b;
        cursor: pointer;
        transition: all 0.15s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.4rem;
      }

      .modal-tab-btn.active {
        background: #ffffff;
        color: #0ca678;
        box-shadow: 0 2px 6px rgba(0,0,0,0.06);
      }

      /* Form Fields matching profile page */
      .modal-form {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
      }

      .modal-form label {
        display: flex;
        flex-direction: column;
        gap: 0.45rem;
      }

      .modal-form label span {
        font-size: 0.85rem;
        font-weight: 700;
        color: #334155;
      }

      .modal-form input[type="text"],
      .modal-form input:not([type]),
      .modal-form textarea {
        width: 100%;
        padding: 0.75rem 1rem;
        border-radius: 12px;
        border: 1.5px solid #d1d5db;
        background: #f8fafc;
        font-size: 0.88rem;
        color: #0f172a;
        box-sizing: border-box;
        font-family: inherit;
        transition: all 0.2s ease;
      }

      .modal-form input[type="text"]:focus,
      .modal-form textarea:focus {
        outline: none;
        border-color: #12b886;
        background: #ffffff;
        box-shadow: 0 0 0 3px rgba(18, 184, 134, 0.15);
      }

      .modal-form textarea {
        resize: vertical;
        min-height: 80px;
      }

      .image-upload-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .image-upload-row input[type="file"] {
        display: none;
      }

      .upload-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.6rem 1.1rem;
        border-radius: 10px;
        background: #f1f5f9;
        border: 1px solid #cbd5e1;
        color: #334155;
        font-weight: 600;
        font-size: 0.82rem;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .upload-btn:hover {
        background: #e2e8f0;
        color: #0f172a;
        border-color: #94a3b8;
      }

      .url-fallback {
        margin-top: 0.4rem;
      }

      .image-preview {
        margin-top: 0.6rem;
        max-height: 120px;
        border-radius: 10px;
        object-fit: cover;
        border: 1px solid #e2e8f0;
      }

      .image-preview.backdrop-preview {
        max-height: 90px;
        width: 100%;
      }

      /* Modal Actions */
      .modal-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;
        margin-top: 1.5rem;
        padding-top: 1.25rem;
        border-top: 1px solid #f1f5f9;
      }

      .ghost-action {
        padding: 0.75rem 1.3rem;
        border-radius: 12px;
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
        color: #475569;
        font-weight: 700;
        font-size: 0.88rem;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .ghost-action:hover {
        background: #e2e8f0;
        color: #0f172a;
      }

      .primary-action {
        padding: 0.75rem 1.5rem;
        border-radius: 12px;
        background: #12b886;
        color: #ffffff;
        font-weight: 700;
        font-size: 0.88rem;
        border: none;
        cursor: pointer;
        box-shadow: 0 4px 14px rgba(18, 184, 134, 0.35);
        transition: all 0.2s ease;
      }

      .primary-action:hover:not(:disabled) {
        background: #0ca678;
        transform: translateY(-1px);
      }

      .primary-action:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        transform: none;
      }

      /* Existing Lists View */
      .klp-list-scroll {
        max-height: 320px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
        padding-right: 0.2rem;
      }

      .klp-list-scroll::-webkit-scrollbar {
        width: 5px;
      }

      .klp-list-scroll::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
      }

      .klp-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.85rem 1.1rem;
        background: #ffffff;
        border: 1.5px solid #e2e8f0;
        border-radius: 14px;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .klp-item:hover {
        background: #f0fdf4;
        border-color: #12b886;
        box-shadow: 0 4px 14px rgba(18, 184, 134, 0.12);
        transform: translateY(-1px);
      }

      .klp-item-left {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        min-width: 0;
      }

      .klp-item-icon {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        background: linear-gradient(135deg, rgba(18, 184, 134, 0.12), rgba(56, 217, 169, 0.15));
        border: 1px solid rgba(18, 184, 134, 0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0ca678;
        flex-shrink: 0;
      }

      .klp-item-text {
        min-width: 0;
      }

      .klp-item-name {
        font-size: 0.9rem;
        font-weight: 700;
        color: #0f172a;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .klp-item-dscr {
        font-size: 0.75rem;
        color: #64748b;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 250px;
      }

      .klp-item-right {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        flex-shrink: 0;
      }

      .klp-item-count {
        font-size: 0.72rem;
        font-weight: 700;
        color: #64748b;
      }

      .klp-add-pill {
        padding: 0.35rem 0.85rem;
        border-radius: 8px;
        border: 1px solid #12b886;
        background: #e6fcf5;
        color: #0ca678;
        font-size: 0.78rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .klp-add-pill:hover {
        background: #12b886;
        color: #ffffff;
      }

      .klp-item-badge.added {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.35rem 0.75rem;
        border-radius: 8px;
        background: #e6fcf5;
        color: #0ca678;
        font-size: 0.75rem;
        font-weight: 700;
      }

      .klp-empty-state {
        text-align: center;
        padding: 2rem 1rem;
      }

      .klp-empty-title {
        font-size: 0.95rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 0.3rem;
      }

      .klp-empty-sub {
        font-size: 0.8rem;
        color: #64748b;
        margin: 0;
      }

      .klp-loading-center {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2.5rem 1rem;
        gap: 0.8rem;
        color: #64748b;
        font-size: 0.85rem;
      }

      .klp-spinner-circle {
        width: 28px;
        height: 28px;
        border: 3px solid #e2e8f0;
        border-top-color: #12b886;
        border-radius: 50%;
        animation: klpSpin .6s linear infinite;
      }

      @keyframes klpSpin {
        to { transform: rotate(360deg); }
      }

      @media (max-width: 640px) {
        .list-modal {
          padding: 1.5rem 1.25rem;
          border-radius: 18px;
        }
      }
    `;
    this.backdropEl.appendChild(style);
  }

  private buildPanelHTML(): string {
    const movieTitle = this.data?.title || this.data?.name || this.data?.original_title || this.data?.original_name || 'this title';
    const isCreate = this.activeTab === 'create';

    let html = `
      <div class="modal-header">
        <div>
          <p class="eyebrow">${isCreate ? 'NEW COLLECTION' : 'ADD TO LIST'}</p>
          <h2>${isCreate ? 'Create New List' : 'Choose a Collection'}</h2>
          <p class="klp-subtitle">${isCreate ? `Create a custom list and automatically save "${this.escapeHtml(movieTitle)}"` : `Add "${this.escapeHtml(movieTitle)}" to your saved collections`}</p>
        </div>
        <button type="button" class="close-button" data-action="close">Close</button>
      </div>`;

    if (!isCreate) {
      // ── EXISTING LISTS VIEW (DEFAULT) ──
      // Top button to create a new list if user prefers
      html += `
        <div class="klp-top-action-bar">
          <button type="button" class="klp-new-list-cta-btn" data-action="tab-create">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            <span>+ Create New List</span>
          </button>
        </div>`;

      if (this.isLoading) {
        html += `
          <div class="klp-loading-center">
            <div class="klp-spinner-circle"></div>
            <span>Loading your collections...</span>
          </div>`;
      } else if (!this.userLists || this.userLists.length === 0) {
        html += `
          <div class="klp-empty-state">
            <div class="klp-empty-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#12b886" stroke-width="1.8">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <h3 class="klp-empty-title">No Collections Yet</h3>
            <p class="klp-empty-sub">Create your first custom list to start organizing movies and TV series.</p>
            <button type="button" class="primary-action" style="margin-top: 1rem;" data-action="tab-create">
              + Create Your First List
            </button>
          </div>`;
      } else {
        html += `<div class="klp-list-scroll">`;
        this.userLists.forEach((list, idx) => {
          const count = list.items?.length || 0;
          const itemId = this.data?.id;
          const alreadyIn = list.items?.some((it: any) =>
            it.item_id === itemId || it.id === itemId || it.movie_data?.id === itemId
          );

          html += `
            <div class="klp-item ${alreadyIn ? 'is-added' : ''}" data-action="add-existing" data-index="${idx}" title="${alreadyIn ? 'Already in this list' : 'Click to add to ' + this.escapeHtml(list.name || 'this list')}">
              <div class="klp-item-left">
                <div class="klp-item-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <div class="klp-item-text">
                  <div class="klp-item-name">${this.escapeHtml(list.name || 'Untitled List')}</div>
                  ${list.dscr || list.description ? `<div class="klp-item-dscr">${this.escapeHtml(list.dscr || list.description)}</div>` : ''}
                </div>
              </div>
              <div class="klp-item-right">
                <span class="klp-item-count">${count} ${count === 1 ? 'title' : 'titles'}</span>
                ${alreadyIn
                  ? `<span class="klp-item-badge added"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> Added</span>`
                  : `<button type="button" class="klp-add-pill" data-action="add-existing" data-index="${idx}">+ Add</button>`
                }
              </div>
            </div>`;
        });
        html += `</div>`;
      }
    } else {
      // ── CREATE NEW LIST FORM ──
      // If user has existing lists, show a Back button
      if (this.userLists && this.userLists.length > 0) {
        html += `
          <div class="klp-back-bar">
            <button type="button" class="klp-back-btn" data-action="tab-existing">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
              <span>Back to My Lists (${this.userLists.length})</span>
            </button>
          </div>`;
      }

      html += `
        <div class="modal-form">
          <label>
            <span>List Name</span>
            <input type="text" data-input="name" placeholder="e.g. Late-Night Sci-Fi & Thrillers" value="${this.escapeHtml(this.createForm.name)}" autofocus />
          </label>

          <label>
            <span>Description (Optional)</span>
            <textarea data-input="dscr" rows="3" placeholder="Why this list exists and what titles belong in it">${this.escapeHtml(this.createForm.dscr)}</textarea>
          </label>

          <label>
            <span>Poster Image (Optional)</span>
            <div class="image-upload-row">
              <input type="file" accept="image/*" data-file="poster" />
              <button type="button" class="ghost-action upload-btn" data-action="pick-poster">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Choose Image
              </button>
            </div>
            <input type="text" data-input="poster-url" placeholder="Or paste poster image URL" class="url-fallback" value="${this.escapeHtml(this.createForm.image)}" />
            ${this.createImagePreview ? `<img src="${this.createImagePreview}" class="image-preview" />` : ''}
          </label>

          <label>
            <span>Backdrop Image (Optional)</span>
            <div class="image-upload-row">
              <input type="file" accept="image/*" data-file="backdrop" />
              <button type="button" class="ghost-action upload-btn" data-action="pick-backdrop">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Choose Image
              </button>
            </div>
            <input type="text" data-input="backdrop-url" placeholder="Or paste backdrop image URL" class="url-fallback" value="${this.escapeHtml(this.createForm.backdrop_image)}" />
            ${this.createBackdropPreview ? `<img src="${this.createBackdropPreview}" class="image-preview backdrop-preview" />` : ''}
          </label>
        </div>

        <div class="modal-actions">
          ${this.userLists && this.userLists.length > 0 ? `<button type="button" class="ghost-action" data-action="tab-existing">Cancel</button>` : `<button type="button" class="ghost-action" data-action="close">Cancel</button>`}
          <button type="button" class="primary-action" data-action="create-list" ${!this.createForm.name.trim() || this.isCreating ? 'disabled' : ''}>
            ${this.isCreating ? 'Creating & Adding...' : 'Create List & Add'}
          </button>
        </div>`;
    }

    return html;
  }

  private bindPanelEvents() {
    if (!this.panelEl) return;

    // Clean previous click handler
    if (this.boundClickHandler) {
      this.panelEl.removeEventListener('click', this.boundClickHandler);
    }
    this.boundClickHandler = (e: Event) => {
      const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement;
      if (!target) return;

      const action = target.dataset['action'];

      switch (action) {
        case 'close':
          this.removePortal();
          this.showPanel = false;
          this.toggleChange.emit();
          break;
        case 'tab-create':
          this.activeTab = 'create';
          this.refreshPanel();
          break;
        case 'tab-existing':
          this.activeTab = 'existing';
          this.refreshPanel();
          break;
        case 'add-existing':
          const idx = parseInt(target.dataset['index'] || '0', 10);
          this.addToList(this.userLists[idx], idx);
          break;
        case 'pick-poster':
          const posterInput = this.panelEl?.querySelector('[data-file="poster"]') as HTMLInputElement;
          posterInput?.click();
          break;
        case 'pick-backdrop':
          const backdropInput = this.panelEl?.querySelector('[data-file="backdrop"]') as HTMLInputElement;
          backdropInput?.click();
          break;
        case 'create-list':
          this.createListAndAdd();
          break;
      }
    };
    this.panelEl.addEventListener('click', this.boundClickHandler);

    // Clean previous input handlers
    this.boundInputHandlers.forEach(h => h.el.removeEventListener(h.event, h.fn));
    this.boundInputHandlers = [];

    // Name Input
    const nameInput = this.panelEl.querySelector('[data-input="name"]') as HTMLInputElement;
    if (nameInput) {
      const fn = (e: Event) => {
        this.createForm.name = (e.target as HTMLInputElement).value;
        const submitBtn = this.panelEl?.querySelector('[data-action="create-list"]') as HTMLButtonElement;
        if (submitBtn) {
          submitBtn.disabled = !this.createForm.name.trim() || this.isCreating;
        }
      };
      nameInput.addEventListener('input', fn);
      this.boundInputHandlers.push({ el: nameInput, event: 'input', fn });
    }

    // Description Textarea
    const dscrInput = this.panelEl.querySelector('[data-input="dscr"]') as HTMLTextAreaElement;
    if (dscrInput) {
      const fn = (e: Event) => {
        this.createForm.dscr = (e.target as HTMLTextAreaElement).value;
      };
      dscrInput.addEventListener('input', fn);
      this.boundInputHandlers.push({ el: dscrInput, event: 'input', fn });
    }

    // Poster URL input
    const posterUrlInput = this.panelEl.querySelector('[data-input="poster-url"]') as HTMLInputElement;
    if (posterUrlInput) {
      const fn = (e: Event) => {
        this.createForm.image = (e.target as HTMLInputElement).value;
        this.createImagePreview = this.createForm.image;
      };
      posterUrlInput.addEventListener('input', fn);
      this.boundInputHandlers.push({ el: posterUrlInput, event: 'input', fn });
    }

    // Backdrop URL input
    const backdropUrlInput = this.panelEl.querySelector('[data-input="backdrop-url"]') as HTMLInputElement;
    if (backdropUrlInput) {
      const fn = (e: Event) => {
        this.createForm.backdrop_image = (e.target as HTMLInputElement).value;
        this.createBackdropPreview = this.createForm.backdrop_image;
      };
      backdropUrlInput.addEventListener('input', fn);
      this.boundInputHandlers.push({ el: backdropUrlInput, event: 'input', fn });
    }

    // File Inputs for Poster & Backdrop
    const posterFileInput = this.panelEl.querySelector('[data-file="poster"]') as HTMLInputElement;
    if (posterFileInput) {
      const fn = async (e: Event) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const url = await compressImageToWebPDataUrl(file, { maxWidth: 800, maxHeight: 1200, quality: 0.82 });
            this.createForm.image = url;
            this.createImagePreview = url;
            this.refreshPanel();
          } catch {
            const reader = new FileReader();
            reader.onload = (ev) => {
              const url = ev.target?.result as string;
              this.createForm.image = url;
              this.createImagePreview = url;
              this.refreshPanel();
            };
            reader.readAsDataURL(file);
          }
        }
      };
      posterFileInput.addEventListener('change', fn);
      this.boundInputHandlers.push({ el: posterFileInput, event: 'change', fn });
    }

    const backdropFileInput = this.panelEl.querySelector('[data-file="backdrop"]') as HTMLInputElement;
    if (backdropFileInput) {
      const fn = async (e: Event) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const url = await compressImageToWebPDataUrl(file, { maxWidth: 1920, maxHeight: 1080, quality: 0.82 });
            this.createForm.backdrop_image = url;
            this.createBackdropPreview = url;
            this.refreshPanel();
          } catch {
            const reader = new FileReader();
            reader.onload = (ev) => {
              const url = ev.target?.result as string;
              this.createForm.backdrop_image = url;
              this.createBackdropPreview = url;
              this.refreshPanel();
            };
            reader.readAsDataURL(file);
          }
        }
      };
      backdropFileInput.addEventListener('change', fn);
      this.boundInputHandlers.push({ el: backdropFileInput, event: 'change', fn });
    }
  }

  private refreshPanel() {
    if (!this.panelEl) return;
    this.panelEl.innerHTML = this.buildPanelHTML();
    this.bindPanelEvents();
    this.applyPanelStyles();
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  private buildItemPayload(): UserListItemPayload | null {
    if (!this.data || !this.data.id) return null;

    const isTv = this.data.media_type === 'tv' || (!this.data.media_type && (!!this.data.first_air_date || (!!this.data.name && !this.data.title)));
    const mediaType = isTv ? 'tv' : 'movie';
    const itemId = this.data.id;
    const title = this.data.title || this.data.name || this.data.original_title || this.data.original_name || 'Title';

    return {
      item_id: itemId,
      type: mediaType,
      date_added: new Date().toISOString(),
      movie_data: {
        id: itemId,
        title: title,
        overview: this.data.overview || '',
        poster_path: this.data.poster_path || '',
        backdrop_path: this.data.backdrop_path || '',
        release_date: this.data.release_date || '',
        first_air_date: this.data.first_air_date || '',
        media_type: mediaType,
        vote_average: this.data.vote_average ?? 0,
      }
    };
  }

  fetchLists() {
    this.isLoading = true;
    this.userListService.getUserLists().subscribe({
      next: (res) => {
        const result = Array.isArray(res) ? res : (res?.result || res?.data || res?.lists || []);
        this.userLists = Array.isArray(result) ? result : [];
        this.isLoading = false;
        if (!this.userLists || this.userLists.length === 0) {
          this.activeTab = 'create';
        } else {
          this.activeTab = 'existing';
        }
        this.refreshPanel();
      },
      error: () => {
        this.userLists = [];
        this.isLoading = false;
        this.activeTab = 'create';
        this.refreshPanel();
      }
    });
  }

  addToList(list: any, index: number) {
    const payload = this.buildItemPayload();
    if (!payload) return;

    const title = payload.movie_data?.title || 'Title';
    const itemId = payload.item_id;

    const alreadyIn = list.items?.some((it: any) =>
      it.item_id === itemId || it.id === itemId || it.movie_data?.id === itemId
    );

    if (alreadyIn) {
      this.toast.info('Already in Collection', `"${title}" is already in "${list.name}".`);
      return;
    }

    this.userListService.addItemToList(payload, index).subscribe({
      next: () => {
        if (!list.items) list.items = [];
        list.items.push(payload);
        this.toast.success('Added to Collection', `"${title}" added to "${list.name}".`);
        this.refreshPanel();
        setTimeout(() => {
          this.removePortal();
          this.showPanel = false;
          this.toggleChange.emit();
        }, 700);
      },
      error: (err) => {
        this.toast.error('Failed to Add', err?.error?.detail || 'Could not add to list.');
      }
    });
  }

  createListAndAdd() {
    const payload = this.buildItemPayload();
    if (!this.createForm.name.trim() || !payload) return;

    const title = payload.movie_data?.title || 'Title';
    this.isCreating = true;
    this.refreshPanel();

    const poster = this.createForm.image || this.data.poster_path || this.data.backdrop_path || '';
    const backdrop = this.createForm.backdrop_image || this.data.backdrop_path || this.data.poster_path || '';

    const listPayload: UserListCreatePayload = {
      name: this.createForm.name.trim(),
      dscr: this.createForm.dscr.trim(),
      image: poster,
      backdrop_image: backdrop,
      type: 'mixed',
      items: [payload],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    this.userListService.createList(listPayload).subscribe({
      next: (res) => {
        const createdList = res?.result || res;
        this.toast.success('Collection Created', `"${title}" added to "${this.createForm.name}".`);
        this.isCreating = false;
        if (this.userLists) {
          this.userLists.push(createdList);
        }
        this.refreshPanel();
        setTimeout(() => {
          this.removePortal();
          this.showPanel = false;
          this.toggleChange.emit();
        }, 700);
      },
      error: (err) => {
        this.toast.error('Failed to Create List', err?.error?.detail || 'Could not create list.');
        this.isCreating = false;
        this.refreshPanel();
      }
    });
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.showPanel) {
      this.removePortal();
      this.showPanel = false;
      this.toggleChange.emit();
    }
  }

  private removePortal() {
    if (this.backdropEl && this.backdropEl.parentNode) {
      this.backdropEl.parentNode.removeChild(this.backdropEl);
    }
    this.backdropEl = null;
    this.panelEl = null;
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('keydown', this.boundKeyHandler);
    }
  }
}
